import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
/**
 * CompensationNumberLine
 * ─────────────────────────────────────────────────────────────────────────────
 * Annotated number line that visualizes the **compensation strategy** for
 * mental arithmetic — the "handige tip" of rounding to a friendly number and
 * then correcting back.
 *
 *   Example: 47 + 39  →  47 + 40 (main jump) − 1 (correction) = 86
 *            47 − 39  →  47 − 40 (main jump) + 1 (correction) = 8
 *
 * Two arrows are drawn above the line:
 *   • mainJump      — the rounded-up step (e.g. +40)
 *   • correction    — the small compensating step (e.g. −1 or +1)
 *
 * Used as **support visual** for REK-HANDIG-PLUS-MIN families and similar
 * "handig rekenen" subskills.
 */
export interface CompensationNumberLineProps {
  /** Starting value (the first operand). */
  start: number;
  /** Final value after both jumps (the answer). */
  end: number;
  /** Intermediate landing point after the main rounded jump. */
  intermediate: number;
  /** Label for the main jump arrow, e.g. "+40" or "−40". */
  mainJumpLabel: string;
  /** Label for the correction jump arrow, e.g. "−1" or "+1". */
  correctionLabel: string;
  /** Optional caption shown above the line (e.g. "47 + 39 = ?"). */
  label?: string;
  /**
   * Hide the value of the end-point so the visual does not spoil the answer.
   * Used when the visual is rendered alongside the question stem.
   */
  hideEndValue?: boolean;
  /** Optional rendering hint — stimulus is more compact; support adds an explanation card. */
  mode?: 'stimulus' | 'support';
}
const dutchNumber = (n: number): string => new Intl.NumberFormat('nl-NL').format(n);
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
/**
 * Map a value onto the [0,100]% axis using the full visible range,
 * padded slightly on both sides so the endpoint markers don't sit on the edge.
 */
function project(value: number, min: number, max: number): number {
  if (max === min) return 50;
  const t = (value - min) / (max - min);
  // Pad 4% on each side
  return 4 + t * 92;
}
export function CompensationNumberLine({
  start,
  end,
  intermediate,
  mainJumpLabel,
  correctionLabel,
  label,
  hideEndValue = false,
  mode = 'support'
}: CompensationNumberLineProps) {
  const computed = useMemo(() => {
    if (!isFiniteNumber(start) || !isFiniteNumber(end) || !isFiniteNumber(intermediate)) {
      return null;
    }
    const values = [start, end, intermediate];
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const span = Math.max(rawMax - rawMin, 1);
    // Pad the axis a bit on both sides so markers don't crowd the edges
    const pad = Math.max(Math.round(span * 0.1), 1);
    return {
      min: rawMin - pad,
      max: rawMax + pad
    };
  }, [start, end, intermediate]);
  if (!computed) {
    return <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200 text-center" data-id="element-11556">
        <p className="text-red-600 font-medium" data-id="element-11557">
          Ongeldige compensatie-getallenlijn configuratie
        </p>
      </div>;
  }
  const {
    min,
    max
  } = computed;
  const startPct = project(start, min, max);
  const intermediatePct = project(intermediate, min, max);
  const endPct = project(end, min, max);
  // Main jump goes start → intermediate
  // Correction jump goes intermediate → end
  const mainLeft = Math.min(startPct, intermediatePct);
  const mainWidth = Math.abs(intermediatePct - startPct);
  const mainDirection = intermediatePct >= startPct ? 'right' : 'left';
  const correctionLeft = Math.min(intermediatePct, endPct);
  const correctionWidth = Math.abs(endPct - intermediatePct);
  const correctionDirection = endPct >= intermediatePct ? 'right' : 'left';
  const isStimulus = mode === 'stimulus';
  const numberLineViz = <div className="relative h-44 mt-4" aria-label="Getallenlijn met compensatie-sprongen" data-id="element-11558">
      {/* ── Main line ──────────────────────────────────────────────────── */}
      <div className="absolute top-[70%] left-0 right-0 h-2 bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full shadow-md -translate-y-1/2" data-id="element-11559" />

      {/* ── Main jump arrow (above line) ───────────────────────────────── */}
      <motion.div initial={{
      opacity: 0,
      scaleX: 0
    }} animate={{
      opacity: 1,
      scaleX: 1
    }} transition={{
      duration: 0.45,
      ease: 'easeOut'
    }} style={{
      left: `${mainLeft}%`,
      width: `${mainWidth}%`,
      transformOrigin: mainDirection === 'right' ? 'left' : 'right'
    }} className="absolute top-[28%]" data-id="element-11560">
        {/* Curved arc representing the jump */}
        <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-8 overflow-visible" data-id="element-11561">
          <path d="M 2 28 Q 50 -8 98 28" fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" data-id="element-11562" />
          {/* Arrow head at landing side */}
          {mainDirection === 'right' ? <polygon points="98,28 92,24 92,32" fill="#4f46e5" data-id="element-11563" /> : <polygon points="2,28 8,24 8,32" fill="#4f46e5" data-id="element-11564" />}
        </svg>
        {/* Label badge */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-indigo-600 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-md whitespace-nowrap" data-id="element-11565">
          {mainJumpLabel}
        </div>
      </motion.div>

      {/* ── Correction arrow (above line, smaller) ─────────────────────── */}
      <motion.div initial={{
      opacity: 0,
      scaleX: 0
    }} animate={{
      opacity: 1,
      scaleX: 1
    }} transition={{
      duration: 0.4,
      ease: 'easeOut',
      delay: 0.35
    }} style={{
      left: `${correctionLeft}%`,
      width: `${Math.max(correctionWidth, 4)}%`,
      transformOrigin: correctionDirection === 'right' ? 'left' : 'right'
    }} className="absolute top-[52%]" data-id="element-11566">
        <svg viewBox="0 0 100 22" preserveAspectRatio="none" className="w-full h-5 overflow-visible" data-id="element-11567">
          <path d="M 4 20 Q 50 -2 96 20" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="0" data-id="element-11568" />
          {correctionDirection === 'right' ? <polygon points="96,20 90,16 90,24" fill="#6366f1" data-id="element-11569" /> : <polygon points="4,20 10,16 10,24" fill="#6366f1" data-id="element-11570" />}
        </svg>
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full bg-indigo-500 text-white px-2.5 py-0.5 rounded-lg text-xs font-bold shadow-md whitespace-nowrap" data-id="element-11571">
          {correctionLabel}
        </div>
      </motion.div>

      {/* ── Markers on the line ────────────────────────────────────────── */}
      <Marker pct={startPct} value={dutchNumber(start)} color="blue" data-id="element-11572" />
      <Marker pct={intermediatePct} value={dutchNumber(intermediate)} color="indigo" variant="intermediate" data-id="element-11573" />
      <Marker pct={endPct} value={hideEndValue ? '?' : dutchNumber(end)} color="emerald" variant={hideEndValue ? 'unknown' : 'end'} data-id="element-11574" />
    </div>;
  if (isStimulus) {
    return <div className="bg-blue-50 rounded-xl px-6 pt-4 pb-10 border border-blue-200" data-id="element-11575">
        {label && <p className="text-sm font-semibold text-blue-800 mb-2 text-center" data-id="element-11576">
            {label}
          </p>}
        {numberLineViz}
      </div>;
  }
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-11577">
      {label && <div className="mb-2 text-center" data-id="element-11578">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-11579">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse" data-id="element-11580" />
            <span className="text-base font-bold text-blue-900" data-id="element-11581">{label}</span>
          </div>
        </div>}

      {numberLineViz}

      <div className="mt-6 bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-11582">
        <div className="flex items-start gap-3" data-id="element-11583">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-11584">
            <span className="text-indigo-600 text-lg" data-id="element-11585">💡</span>
          </div>
          <div data-id="element-11586">
            <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-11587">
              Handige tip: rond eerst af, corrigeer daarna
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-11588">
              Begin bij <strong data-id="element-11589">{dutchNumber(start)}</strong>, maak eerst de
              grote sprong{' '}
              <strong className="text-indigo-700" data-id="element-11590">{mainJumpLabel}</strong> naar{' '}
              <strong data-id="element-11591">{dutchNumber(intermediate)}</strong>, en corrigeer dan met
              de kleine sprong{' '}
              <strong className="text-indigo-700" data-id="element-11592">{correctionLabel}</strong>
              {!hideEndValue && <>
                  {' '}
                  om uit te komen op <strong data-id="element-11593">{dutchNumber(end)}</strong>
                </>}
              .
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// ─── Marker subcomponent ────────────────────────────────────────────────────
interface MarkerProps {
  pct: number;
  value: string;
  color: 'blue' | 'indigo' | 'emerald';
  variant?: 'start' | 'intermediate' | 'end' | 'unknown';
}
const COLOR_STYLES: Record<MarkerProps['color'], {
  dot: string;
  label: string;
}> = {
  blue: {
    dot: 'bg-blue-600 ring-blue-200',
    label: 'text-blue-900 border-blue-200'
  },
  indigo: {
    dot: 'bg-indigo-500 ring-indigo-200',
    label: 'text-indigo-900 border-indigo-200'
  },
  emerald: {
    dot: 'bg-emerald-600 ring-emerald-200',
    label: 'text-emerald-900 border-emerald-200'
  }
};
function Marker({
  pct,
  value,
  color,
  variant = 'start'
}: MarkerProps) {
  const styles = COLOR_STYLES[color];
  const isUnknown = variant === 'unknown';
  return <motion.div initial={{
    scale: 0,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 400,
    damping: 24,
    delay: 0.1
  }} className="absolute top-[70%] -translate-y-1/2 -translate-x-1/2 z-10" style={{
    left: `${pct}%`
  }} data-id="element-11594">
      <div className={`w-5 h-5 rounded-full border-2 border-white shadow-lg ring-4 ${styles.dot} ${isUnknown ? 'animate-pulse' : ''}`} data-id="element-11595" />
      <div className={`absolute top-7 left-1/2 -translate-x-1/2 text-xs font-bold bg-white/90 px-2 py-0.5 rounded-lg border shadow-sm whitespace-nowrap ${styles.label}`} data-id="element-11596">
        {value}
      </div>
    </motion.div>;
}